import { useState, useRef, useEffect } from "react";
import { MessageSquare, Bug, Lightbulb, X, Loader2, CheckCircle, ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Turnstile from "@/components/Turnstile";

import { BACKEND_URL } from "@/config/runtime";

const FEEDBACK_TYPES = [
  { 
    id: "feedback", 
    label: "General Feedback", 
    icon: MessageSquare, 
    description: "Share your thoughts or suggestions",
    color: "text-blue-600 bg-blue-50 border-blue-200"
  },
  { 
    id: "bug", 
    label: "Report a Bug", 
    icon: Bug, 
    description: "Something isn't working correctly",
    color: "text-red-600 bg-red-50 border-red-200"
  },
  { 
    id: "feature_request", 
    label: "Feature Request", 
    icon: Lightbulb, 
    description: "Suggest a new feature or improvement",
    color: "text-amber-600 bg-amber-50 border-amber-200"
  }
];

export function FeedbackModal({ open, onOpenChange, defaultType = null }) {
  const [feedbackType, setFeedbackType] = useState(defaultType);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [screenshots, setScreenshots] = useState([]);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const fileInputRef = useRef(null);

  const MAX_SCREENSHOTS = 5;

  // Update feedbackType when defaultType changes (e.g., from sidebar)
  useEffect(() => {
    if (defaultType) {
      setFeedbackType(defaultType);
    }
  }, [defaultType]);

  const resetForm = () => {
    setFeedbackType(defaultType || null);
    setSubject("");
    setDescription("");
    setScreenshots([]);
    setIsSuccess(false);
  };

  const handleScreenshotUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remainingSlots = MAX_SCREENSHOTS - screenshots.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_SCREENSHOTS} screenshots allowed`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    setIsUploadingScreenshot(true);

    try {
      const uploadPromises = filesToUpload.map(async (file) => {
        // Validate file type
        if (!file.type.startsWith("image/")) {
          throw new Error(`${file.name} is not an image`);
        }
        
        // Validate file size (max 5MB per image)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} is too large (max 5MB)`);
        }

        // Use FormData for multipart upload (more efficient than base64)
        const formData = new FormData();
        formData.append('file', file);

        // Upload with automatic retry
        let response;
        const maxRetries = 3;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            response = await fetch(`${BACKEND_URL}/api/upload/image/multipart`, {
              method: "POST",
              credentials: "include",
              body: formData
            });
            if (response) break;
          } catch (fetchError) {
            if (attempt < maxRetries) {
              await new Promise(r => setTimeout(r, 1000 * attempt));
            }
          }
        }

        if (!response || !response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        const data = await response.json();
        return data.url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setScreenshots(prev => [...prev, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} screenshot(s) uploaded`);
    } catch (error) {
      console.error("Screenshot upload error:", error);
      toast.error(error.message || "Failed to upload screenshot");
    } finally {
      setIsUploadingScreenshot(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form after close animation
    setTimeout(resetForm, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedbackType || !subject.trim() || !description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!turnstileToken) {
      toast.error("Please complete the CAPTCHA verification");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          feedback_type: feedbackType,
          subject: subject.trim(),
          description: description.trim(),
          page_url: window.location.href,
          browser_info: navigator.userAgent,
          screenshots: screenshots,
          turnstile_token: turnstileToken
        })
      });

      if (response.ok) {
        setIsSuccess(true);
        toast.success("Thank you for your feedback!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit feedback");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#1E3A2F]">
            {isSuccess ? "Feedback Submitted!" : "Share Your Feedback"}
          </DialogTitle>
          <DialogDescription>
            {isSuccess 
              ? "We appreciate you taking the time to help us improve."
              : "Help us improve CampYid by sharing your thoughts or reporting issues."
            }
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-800" />
            </div>
            <p className="text-[#2D5A3D] mb-6">
              Your feedback has been received. We'll review it shortly.
            </p>
            <Button 
              onClick={handleClose}
              className="bg-[#3D6B4A] hover:bg-[#1E3A2F]"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Feedback Type Selection */}
            <div className="space-y-3">
              <Label className="text-[#1E3A2F]">What type of feedback is this? *</Label>
              <div className="grid grid-cols-1 gap-2">
                {FEEDBACK_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = feedbackType === type.id;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFeedbackType(type.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                        isSelected 
                          ? type.color + " border-current" 
                          : "bg-white border-stone-200 hover:border-stone-300"
                      }`}
                      data-testid={`feedback-type-${type.id}`}
                    >
                      <div className={`p-2 rounded-md ${isSelected ? "" : "bg-stone-100"}`}>
                        <Icon className={`w-5 h-5 ${isSelected ? "" : "text-stone-700"}`} />
                      </div>
                      <div>
                        <p className={`font-medium ${isSelected ? "" : "text-stone-800"}`}>
                          {type.label}
                        </p>
                        <p className={`text-xs ${isSelected ? "opacity-80" : "text-stone-700"}`}>
                          {type.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject" className="text-[#1E3A2F]">Subject *</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={
                  feedbackType === "bug" 
                    ? "Brief description of the issue" 
                    : feedbackType === "feature_request"
                    ? "What feature would you like?"
                    : "What's on your mind?"
                }
                className="border-stone-300 focus:border-[#6B8E6B] focus:ring-[#6B8E6B]"
                data-testid="feedback-subject-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-[#1E3A2F]">
                {feedbackType === "bug" ? "Steps to reproduce / Details *" : "Description *"}
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  feedbackType === "bug"
                    ? "Please describe what happened, what you expected, and the steps to reproduce the issue..."
                    : feedbackType === "feature_request"
                    ? "Describe the feature and how it would help you..."
                    : "Share your feedback in detail..."
                }
                rows={5}
                className="border-stone-300 focus:border-[#6B8E6B] focus:ring-[#6B8E6B] resize-none"
                data-testid="feedback-description-input"
              />
            </div>

            {/* Screenshots Upload - Only show for bug reports */}
            {feedbackType === "bug" && (
              <div className="space-y-3">
                <Label className="text-[#1E3A2F]">
                  Screenshots <span className="text-stone-600 font-normal">(optional, up to {MAX_SCREENSHOTS})</span>
                </Label>
                
                {/* Screenshot Preview Grid */}
                {screenshots.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {screenshots.map((url, index) => (
                      <div 
                        key={index} 
                        className="relative group aspect-video bg-stone-100 rounded-lg overflow-hidden border border-stone-200"
                      >
                        <img 
                          src={url.startsWith('http') ? url : `${BACKEND_URL}${url}`} 
                          alt={`Screenshot ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeScreenshot(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-screenshot-${index}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                {screenshots.length < MAX_SCREENSHOTS && (
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleScreenshotUpload}
                      accept="image/*"
                      multiple
                      className="hidden"
                      data-testid="screenshot-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingScreenshot}
                      className="w-full border-dashed border-stone-300 hover:border-[#6B8E6B] hover:bg-stone-50"
                      data-testid="upload-screenshot-btn"
                    >
                      {isUploadingScreenshot ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <ImagePlus className="w-4 h-4 mr-2" />
                          Add Screenshots ({screenshots.length}/{MAX_SCREENSHOTS})
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-stone-600 mt-1.5">
                      Attach screenshots to help us understand the issue better. Max 5MB per image.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* CAPTCHA */}
            <div className="flex justify-center">
              <Turnstile
                onVerify={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken("")}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#3D6B4A] hover:bg-[#1E3A2F]"
                disabled={isSubmitting || !feedbackType || !subject.trim() || !description.trim() || !turnstileToken}
                data-testid="submit-feedback-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Feedback"
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
